/**
 * File: LegalResearchDashboard.jsx
 * Path: /src/components/solutions/LegalResearchDashboard.jsx
 * Description: Standalone dashboard visualization for the Legal Research AI solution
 * Key Features: Interactive legal search visualization with query and results display
 */

import React from 'react';
import { motion } from 'framer-motion';

const LegalResearchDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-primary-700 to-secondary-700 p-6">
        {/* Circuit pattern */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
            </g>
            <g fill="rgba(255,255,255,0.5)">
              <circle cx="492.5" cy="224.5" r="4" />
              <circle cx="365.5" cy="196.5" r="4" />
              <circle cx="627.5" cy="358.5" r="4" />
              <circle cx="200.5" cy="144.5" r="4" />
              <circle cx="549.5" cy="421.5" r="4" />
            </g>
          </svg>
        </div>
        
        {/* Dashboard container with min-height to prevent cutting off */}
        <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 w-full text-white">
            {/* Search Interface */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl">Veritect Legal Research AI</h3>
              </div>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            <div className="relative mb-4">
              <input 
                type="text" 
                className="w-full bg-white/5 border border-white/20 rounded-lg p-3 pr-12 text-white placeholder-white/50" 
                placeholder="What are the elements required for a valid contract in California?"
                disabled
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center animate-pulse">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Results Preview */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-xs text-white/60 mb-1">Key Elements</div>
                <ul className="text-sm space-y-1">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent-400 rounded-full mr-2"></div>
                    <span>Offer and Acceptance</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent-400 rounded-full mr-2"></div>
                    <span>Consideration</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent-400 rounded-full mr-2"></div>
                    <span>Legal Purpose</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-accent-400 rounded-full mr-2"></div>
                    <span>Capacity to Contract</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-xs text-white/60 mb-1">Key Cases</div>
                <ul className="text-sm space-y-1">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-2"></div>
                    <span>Robinson v. Magee (2022)</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-2"></div>
                    <span>California Civil Code § 1550</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-2"></div>
                    <span>Donovan v. RRL Corp (2001)</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-2"></div>
                    <span>C&H Foods v. Hartford (2012)</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Progress Indicators */}
            <div className="flex space-x-4">
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Relevance</div>
                <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="w-5/6 h-1.5 bg-accent-500 rounded-full"></div>
                </div>
              </div>
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Cases Analyzed</div>
                <div className="text-xs font-medium">5,428</div>
              </div>
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Time Saved</div>
                <div className="text-xs font-medium">3.2 hours</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalResearchDashboard;